<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-va-optimization">
    <div class="optimization-page">
      <div class="wy-va-opitization-main-content">
        <div class="wy-va-optimization-small-title">
          {{$t(currentPageInfo.subtitle)}}
        </div>
        <div class="wy-va-optimization-title">
          Overview
        </div>
        <div>      
          <div v-if="pagePanels.chartsTable && !chartDataInfo.noData" class="wy-va-opitization-chart-section" v-loading="chartDataInfo.loading">
            <!-- pie charts -->
            <div class="optimization-details">
              <table v-if="performanceMetricData" ref="pies" class="optimization-details-table" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td>
                    <wy-va-optimization-pie-chart :title="performanceMetricData.understanding[0].label" :parent="module" traceKey="pieUnderstanding" :traceList="performanceMetricData.understanding" :titleToolTipContent="performanceMetricData.understanding[0].toolTipContent" :targetKey="'understanding'" />
                  </td>
                  <td>
                    <wy-va-optimization-pie-chart :title="performanceMetricData.containment[0].label" :parent="module" traceKey="pieContainment" :traceList="performanceMetricData.containment" :titleToolTipContent="performanceMetricData.containment[0].toolTipContent" :targetKey="'containment'"  />
                  </td>
                  <td>
                    <wy-va-optimization-pie-chart :title="performanceMetricData.negativeFeedbackScore[0].label" :parent="module" traceKey="pieNegativeFeedbackScore" :traceList="performanceMetricData.negativeFeedbackScore" :titleToolTipContent="performanceMetricData.negativeFeedbackScore[0].toolTipContent" :targetKey="'negativeFeedbackScore'" />
                  </td>
                  <!-- hide for prod deployment OC-388 request -->
                  <!-- <td>
                    <wy-va-optimization-pie-chart :title="performanceMetricData.engagement[0].label" :parent="module" traceKey="pieEngagement" :traceList="performanceMetricData.engagement" :titleToolTipContent="performanceMetricData.engagement[0].toolTipContent" :targetKey="'engagement'" />
                  </td> -->
                </tr>
              </table>
            </div>
            <!-- line charts -->
            <div class="chart optimizationTrendChart">
              <wy-va-optimization-line-chart v-if="haslineChartData" ref="optimizationChart" :data="optimizationChartData" />
            </div>
          </div>
          <div v-else-if="pagePanels.chartsTable" class="no-result-found" v-loading="chartDataInfo.loading">
            <div class="no-data"></div>
            <div class="no-data-title">{{$t('noResultFound.title')}}</div>
            <div class="no-data-content">
              <div class="content-text">
                {{$t('noResultFound.message')}}
              </div>
            </div>
          </div>

          <div class="wy-optimization-table-section">
            <wy-table-list v-if="pagePanels.conversationsTable && conversationsTableColumns[0]" ref="conversationsTable" :controller="module" :noData="conversationInfo.noData" :panelLoading="conversationInfo.loading" :tableName="`conversations`" :tableDataResults="conversationsResults" :defaultHeaders="setDefaultHeader(conversationsTableColumns)" :tableColumns="conversationsTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(conversationsColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('conversations')" />
            
            <wy-table-list v-if="pagePanels.containedTable && containedTableColumns[0]" ref="containedTable" :controller="module" :noData="containedInfo.noData" :panelLoading="containedInfo.loading" :tableName="`contained`" :tableDataResults="containedResults" :defaultHeaders="setDefaultHeader(containedTableColumns)" :tableColumns="containedTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(containedColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('contained')" />
            
            <wy-table-list v-if="pagePanels.handedOffTable && handedOffTableColumns[0]" ref="handedOffTable" :controller="module" :noData="handOffInfo.noData" :panelLoading="handOffInfo.loading" :tableName="`handedOff`" :tableDataResults="handOffResults" :defaultHeaders="setDefaultHeader(handedOffTableColumns)" :tableColumns="handedOffTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(handedOffColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('handedOff')" />
            
            <wy-table-list v-if="pagePanels.corpusTable && corpusTableColumns[0]" ref="corpusTable" :controller="module" :noData="corpusInfo.noData" :panelLoading="corpusInfo.loading" :tableName="`corpus`" :tableDataResults="corpusResults" :defaultHeaders="setDefaultHeader(corpusTableColumns)" :tableColumns="corpusTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(corpusColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('corpus')" />
            
            <wy-table-list v-if="pagePanels.responseTable && responseTableColumns[0]" ref="responseTable" :controller="module" :noData="responseInfo.noData" :panelLoading="responseInfo.loading" :tableName="`response`" :tableDataResults="responseResults" :defaultHeaders="setDefaultHeader(responseTableColumns)" :tableColumns="responseTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(responseColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('response')" />
            
            <wy-table-list v-if="pagePanels.feedbackTable && feedbackTableColumns[0]" ref="feedbackTable" :controller="module" :noData="feedbackInfo.noData" :panelLoading="feedbackInfo.loading" :tableName="`feedback`" :tableDataResults="feedbackResults" :defaultHeaders="setDefaultHeader(feedbackTableColumns)" :tableColumns="feedbackTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(feedbackColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('feedback')" />
            
            <wy-table-list v-if="pagePanels.issuesTable && issuesTableColumns[0]" ref="issuesTable" :controller="module" :noData="issuesInfo.noData" :panelLoading="issuesInfo.loading" :tableName="`issues`" :tableDataResults="issuesResults" :defaultHeaders="setDefaultHeader(issuesTableColumns)" :tableColumns="issuesTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(issuesColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('issues')" />
            
            <wy-table-list v-if="pagePanels.nluTrainingTable && nluTrainingTableColumns[0]" ref="nluTrainingTable" :controller="module" :noData="nluTrainingInfo.noData" :panelLoading="nluTrainingInfo.loading" :tableName="`nluTraining`" :defaultHeaders="setDefaultHeader(nluTrainingResults)" :tableDataResults="nluTrainingResults" :tableColumns="nluTrainingTableColumns" :enableSelection="true" :hasSubtitle = hasSubtitle(nluTrainingColumns) :defaultSort="{name: 'total', type: 1}" :mode="'generic'" :noDataMsg="'Data not available.'" :defaultPanelHeight="getTableBodyHeight('nluTraining')" />
          </div>
        </div>
      </div>
    </div>
  </div>
  
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import wyVaOptimizationPieChart from '../optimization/wy-va-optimization-pie-chart';
import wyVaOptimizationLineChart from '../optimization/wy-va-optimization-line-chart';
import WyTableList from '../widgets/wy-table-list-v2';
import WyVaOptimizationUtils from '../optimization/utils/wy-va-optimization-utils.js';
import WyUtils from '../../library/wy-utils';

export default Vue.extend({
  name: 'wy-va-optimization-page',
  components: {
    'wy-va-optimization-pie-chart': wyVaOptimizationPieChart,
    'wy-table-list': WyTableList,
    'wy-va-optimization-line-chart': wyVaOptimizationLineChart
  },

  data() {
    return {
      module: this,
      loading: false,

      chartDataInfo: {
        loading: false,
        noData: false
      },
      conversationInfo: {
        loading: false,
        noData: false
      },
      containedInfo: {
        loading: false,
        noData: false
      },
      feedbackInfo: {
        loading: false,
        noData: false
      },
      nluTrainingInfo: {
        loading: false,
        noData: false
      },
      handOffInfo: {
        loading: false,
        noData: false
      },
      corpusInfo: {
        loading: false,
        noData: false
      },
      responseInfo: {
        loading: false,
        noData: false
      },
      issuesInfo: {
        loading: false,
        noData: false
      },

      loadTables: false,
      showTable: false,
      initDate: null,
      pagePanels: null,
      
      optimizationChartData: null,
      performanceMetricData: null,

      targetType: 'total',

      conversationsColumns: [
        {label: "Conversations", name: "conversations", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total VA volumes', cssClass: 'conversations', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      containedColumns: [
        {label: "Contained", name: "contained", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Contained conversations', cssClass: 'contained', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      handedOffColumns: [
        {label: "Handed Off", name: "handedOff", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Handed Off Conversations', cssClass: 'handedOff', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      understandingColumns: [
        {label: "Understanding", name: "understanding", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Understanding', cssClass: 'understanding', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      goalsColumns: [
        {label: "Goals", name: "goals", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Responses', cssClass: 'goals', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      feedbackColumns: [
        {label: "Feedback", name: "feedback", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Feedback', cssClass: 'feedback', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      issuesColumns: [
        {label: "Issues", name: "issues", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Issues', cssClass: 'issues', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      customerBehaviourColumns: [
        {label: "Customer Behaviour", name: "customerBehaviour", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Customer Behaviour', cssClass: 'customerBehaviour', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      corpusColumns: [
        {label: "Corpus", name: "corpus", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Corpus', cssClass: 'corpus', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      responseColumns: [
        {label: "Response", name: "response", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Response', cssClass: 'response', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],
      nluTrainingColumns: [
        {label: "NLU Training", name: "nluTraining", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'NLU Training', cssClass: 'nluTraining', align:"left", minWidth: 300, fixed: true, copyable: false, isEditTopicName: false},
        {label: "Target", name: "target", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Target', cssClass: 'target', align:"center", minWidth: 70, fixed: false, copyable: false, isEditTopicName: false},
        {label: "Total", name: "total", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Total', cssClass: 'total', minWidth: 50, fixed: false, copyable: false, isEditTopicName: false, hasIcon: true},
        {label: "Trend", name: "trendGraph", mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Trend', cssClass: 'trend', align:"centre", minWidth: 150, fixed: false, copyable: false, isEditTopicName: false},
      ],

      conversationsTableColumns: [],
      containedTableColumns: [],
      handedOffTableColumns: [],
      understandingTableColumns: [],
      goalsTableColumns: [],
      feedbackTableColumns: [],
      issuesTableColumns: [],
      customerBehaviourTableColumns: [],
      corpusTableColumns: [],
      responseTableColumns: [],
      nluTrainingTableColumns: [],

      chartDataResults:[],
      conversationsResults: [],
      containedResults: [],
      messagesResults: [],
      feedbackResults: [],
      corpusResults: [],
      handOffResults: [],
      responseResults: [],
      issuesResults: [],
      nluTrainingResults: [],

      allSummaryResults: [],

      containedConversationWithBotIndentList:[                    
        'Received positive feedback', 
        'Received negative feedback'
      ],
      conversationsHandedOffIndentList:[
        'Customer requested handoff', 
        'Automatic handoff after intent-based trigger'
      ],
      customerMsgsUnderstoodIndentList:[
        'False-positive rate',
        'Customer messages with DYM',
      ],
      customerMsgsNotUnderstoodIndentList:[
        'Candidates for new intents', 
        'Candidates for existing intents',
        'Out of domain'
      ],

      optimizationRawChartData: null
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'currentPageInfo', 'companyConfig','globalConfig', 'tableModel', 'isLoggedIn']),

    haslineChartData() {
      return this.optimizationChartData && this.optimizationChartData.dateList.length > 0;
    }
  },

  watch: {
    'loading': function(val) {
      setTimeout(()=>{
        val ? this.showTable = false : this.showTable = true
      }, 300)
    }
  },

  methods: {
    ...mapActions(['fetchModelStats', 'trafficManager', 'fetchMetrics', 'fetchOptimizationTableData', 'fetchOptimizationCorpusSummary',
    'fetchOptimizationChartData', 'fetchOptimizationOverViewConversations', 'fetchOptimizationOverViewContained', 'fetchOptimizationOverViewFeedback', 
    'fetchOptimizationOverViewUnderstanging', 'fetchOptimizationOverViewNluTraning', 'fetchOptimizationOverViewCorpus', 'fetchOptimizationOverViewHandedOff', 
    'fetchOptimizationOverViewIssues','fetchOptimizationOverViewResponses']),

    setDefaultHeader(columns) {
      let defaultHeader = []
      if(columns && columns.length > 0) {
        columns.forEach(column => {
          let header = {
            label: column.group
          }
          defaultHeader.push(header)
        })
      }
      return defaultHeader;
    },
    refreshTable() {
      this.$refs.table.refreshTable();
    },
    handleIndenTableData(tableData, panelName){
      tableData.forEach(el => {
        el.hasIndent = false;
        if(this.containedConversationWithBotIndentList.includes(el[panelName]) && this.globalConfig && this.globalConfig.appPages && this.globalConfig.appPages.vaoOverViewPage && this.globalConfig.appPages.vaoOverViewPage.metrics && this.globalConfig.appPages.vaoOverViewPage.metrics.containedWithBotRequestedFeedback !== false){
          el.hasIndent = true;
        }
        if(this.conversationsHandedOffIndentList.includes(el[panelName]) && this.globalConfig && this.globalConfig.appPages && this.globalConfig.appPages.vaoOverViewPage && this.globalConfig.appPages.vaoOverViewPage.metrics && this.globalConfig.appPages.vaoOverViewPage.metrics.conversationsHandedOffAkaHandoffs !== false){
          el.hasIndent = true;
        }

        if(this.customerMsgsUnderstoodIndentList.includes(el[panelName]) && this.globalConfig && this.globalConfig.appPages && this.globalConfig.appPages.vaoOverViewPage && this.globalConfig.appPages.vaoOverViewPage.metrics && this.globalConfig.appPages.vaoOverViewPage.metrics.nluTrainingMessagesUnderstood !== false){
          el.hasIndent = true;
        }
        
        if(this.customerMsgsNotUnderstoodIndentList.includes(el[panelName]) && this.globalConfig && this.globalConfig.appPages && this.globalConfig.appPages.vaoOverViewPage && this.globalConfig.appPages.vaoOverViewPage.metrics && this.globalConfig.appPages.vaoOverViewPage.metrics.nluTrainingMessagesNotUnderstood !== false){
          el.hasIndent = true;
        }
      })
      return tableData;
    },
    handleTableConditions(){
      if(this.globalConfig && this.globalConfig.appPages && this.globalConfig.appPages.vaoOverViewPage && this.globalConfig.appPages.vaoOverViewPage.metrics && this.globalConfig.appPages.vaoOverViewPage.metrics.conversationsThatReceivedNegativeFeedback == false && this.globalConfig.appPages.vaoOverViewPage.metrics.conversationsThatReceivedPositiveFeedback == false && this.globalConfig.appPages.vaoOverViewPage.metrics.conversationsWithBotRequestedFeedback == false){
        if(this.companyConfig && this.companyConfig.appPages && this.companyConfig.appPages.vaoOverViewPage && this.companyConfig.appPages.vaoOverViewPage.metrics && this.companyConfig.appPages.vaoOverViewPage.metrics.conversationsThatReceivedNegativeFeedback == false && this.companyConfig.appPages.vaoOverViewPage.metrics.conversationsThatReceivedPositiveFeedback == false && this.companyConfig.appPages.vaoOverViewPage.metrics.conversationsWithBotRequestedFeedback == false){
          return false;
        }
        return true;
      }
      else if(!(this.companyConfig && this.companyConfig.appPages && this.companyConfig.appPages.vaoOverViewPage && this.companyConfig.appPages.vaoOverViewPage.metrics && this.companyConfig.appPages.vaoOverViewPage.metrics.conversationsThatReceivedNegativeFeedback == false && this.companyConfig.appPages.vaoOverViewPage.metrics.conversationsThatReceivedPositiveFeedback == false && this.companyConfig.appPages.vaoOverViewPage.metrics.conversationsWithBotRequestedFeedback == false)){
        return true;
      }
      return false;
    },
    hasSubtitle(columns) {
      let hasSubtitle = false;
      columns.forEach(column => {
        hasSubtitle = column.hasSubtitle;
      });
      return hasSubtitle;
    },

    changeCompareBy() {
      this.handleFilterChange();
    },

    changeDateRange() {
      this.handleFilterChange();
    },

    getTableBodyHeight(tableName) {
      let height = this.getTableRecord(tableName) * 62
      return height
    },

    getTableRecord(tableName) {
      let vaoOverViewPageMetrics = this.companyConfig ? (((this.companyConfig.appPages || {}).vaoOverViewPage || {}).metrics || {}) : {};
      let tableFieldsMap = WyVaOptimizationUtils.tablesMap[tableName];
      let keys = []
      Object.keys(tableFieldsMap).forEach(key=>{
        if(vaoOverViewPageMetrics[key]===undefined || vaoOverViewPageMetrics[key] === true) {
          keys.push(key)
        }
      })
      return keys.length
    },

    mappingTableDataByName(tableName, list) {
      let vaoOverViewPageMetrics = this.companyConfig ? (((this.companyConfig.appPages || {}).vaoOverViewPage || {}).metrics || {}) : {};
      let tableFieldsMap = WyVaOptimizationUtils.tablesMap[tableName];
      let tableData = [];
      let isNodata = true;
      Object.keys(tableFieldsMap).forEach(key=>{
        list.forEach(item=>{
          if(key == item.name && (vaoOverViewPageMetrics[key]===undefined || vaoOverViewPageMetrics[key] === true)) {
            item[tableName] = tableFieldsMap[key];
            tableData.push(item)
          }
        });
      })
      tableData.forEach(data => {
        if(data.trend.length > 0) {
          isNodata = false;
        }
      });
      if(isNodata) {
        tableData = [];
      }
      return tableData;
    },

    async handleFilterChange() {
      if((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
        this.loading = true;
        
        if(this.pagePanels.chartsTable) {
          this.chartDataInfo.loading = true
          this.chartDataInfo.noData = false
          this.fetchOptimizationChartData().then((results)=>{
            this.chartDataInfo.loading = false
            this.performanceMetricData = results.totalPerformanceMetricData;  //pies
            this.optimizationChartData = results.trendPerformanceMetricData;  //lines
            if(!this.optimizationChartData || !this.optimizationChartData.dateList || this.optimizationChartData.dateList.length == 0) {
              this.chartDataInfo.noData = true
            }
            else {
              setTimeout(() => {
                if(this.$refs.optimizationChart) {
                  this.$refs.optimizationChart.buildChart(this.optimizationChartData);
                }
              }, 300);
            }
          })
        }
        
        if(this.pagePanels.conversationsTable) {
          this.conversationsResults = [];
          this.conversationInfo.loading = true
          this.conversationInfo.noData = false;
          this.fetchOptimizationOverViewConversations().then((results)=>{
            this.conversationInfo.loading = false
            if(results.length == 0) {
              this.conversationInfo.noData = true;
            }
            this.conversationsResults = this.handleIndenTableData(this.mappingTableDataByName(this.conversationsTableColumns[0].name, results), 'conversations')
          })
        }
        
        if(this.pagePanels.containedTable) {
          this.containedResults = []
          this.containedInfo.loading = true
          this.containedInfo.noData = false;
          this.fetchOptimizationOverViewContained().then((results)=>{
            this.containedInfo.loading = false
            if(results.length == 0) {
              this.containedInfo.noData = true;
            }
            this.containedResults = this.handleIndenTableData(this.mappingTableDataByName(this.containedTableColumns[0].name, results), 'contained')
          })
        }
        
        if(this.pagePanels.feedbackTable) {
          this.feedbackResults = []
          this.feedbackInfo.loading = true
          this.feedbackInfo.noData = false;
          this.fetchOptimizationOverViewFeedback().then((results)=>{
            this.feedbackInfo.loading = false
            if(results.length == 0) {
              this.feedbackInfo.noData = true;
            }
            this.feedbackResults = this.handleIndenTableData(this.mappingTableDataByName(this.feedbackTableColumns[0].name, results), 'feedback');
          })
        }
        
        if(this.pagePanels.nluTrainingTable) {
          this.nluTrainingResults = [];
          this.nluTrainingInfo.loading = true
          this.nluTrainingInfo.noData = false;
          this.fetchOptimizationOverViewNluTraning().then((results)=>{
            this.nluTrainingInfo.loading = false
            if(results.length == 0) {
              this.nluTrainingInfo.noData = true;
            }
            this.nluTrainingResults = this.handleIndenTableData(this.mappingTableDataByName(this.nluTrainingTableColumns[0].name, results), 'nluTraining')  
          })
        }
        
        if(this.pagePanels.corpusTable) {
          this.corpusResults = []
          this.corpusInfo.loading = true
          this.corpusInfo.noData = false;
          this.fetchOptimizationOverViewCorpus().then((results)=>{
            this.corpusInfo.loading = false
            if(results.length == 0) {
              this.corpusInfo.noData = true;
            }
            this.corpusResults = this.handleIndenTableData(this.mappingTableDataByName(this.corpusTableColumns[0].name, results), 'corpus')
          })
        }
        
        if(this.pagePanels.handedOffTable) {
          this.handOffResults = []
          this.handOffInfo.loading = true
          this.handOffInfo.noData = false;
          this.fetchOptimizationOverViewHandedOff().then((results)=>{
            this.handOffInfo.loading = false
            if(results.length == 0) {
              this.handOffInfo.noData = true;
            }
            this.handOffResults = this.handleIndenTableData(this.mappingTableDataByName(this.handedOffTableColumns[0].name, results), 'handedOff')
          })
        }
        
        if(this.pagePanels.responseTable) {
          this.responseResults = [];
          this.responseInfo.loading = true
          this.responseInfo.noData = false;
          this.fetchOptimizationOverViewResponses().then((results)=>{
            this.responseInfo.loading = false
            if(results.length == 0) {
              this.responseInfo.noData = true;
            }
            this.responseResults = this.handleIndenTableData(this.mappingTableDataByName(this.responseTableColumns[0].name, results), 'response')
          })
        }
        
        if(this.pagePanels.issuesTable) {
          this.issuesResults = []
          this.issuesInfo.loading = true
          this.issuesInfo.noData = false;
          this.fetchOptimizationOverViewIssues().then((results)=>{
            this.issuesInfo.loading = false
            if(results.length == 0) {
              this.issuesInfo.noData = true;
            }
            this.issuesResults = this.handleIndenTableData(this.mappingTableDataByName(this.issuesTableColumns[0].name, results), 'issues')
          })
        }

        this.conversationsTableColumns = JSON.parse(JSON.stringify(this.conversationsColumns));
        this.containedTableColumns = JSON.parse(JSON.stringify(this.containedColumns));
        this.handedOffTableColumns = JSON.parse(JSON.stringify(this.handedOffColumns));
         //Hide by OC-556 base on OC-418
        // this.understandingTableColumns = JSON.parse(JSON.stringify(this.understandingColumns));
        this.goalsTableColumns = JSON.parse(JSON.stringify(this.goalsColumns));
        this.feedbackTableColumns = JSON.parse(JSON.stringify(this.feedbackColumns));
        this.issuesTableColumns = JSON.parse(JSON.stringify(this.issuesColumns));
        this.customerBehaviourTableColumns = JSON.parse(JSON.stringify(this.customerBehaviourColumns));
        this.corpusTableColumns = JSON.parse(JSON.stringify(this.corpusColumns));
        this.responseTableColumns = JSON.parse(JSON.stringify(this.responseColumns));
        this.nluTrainingTableColumns = JSON.parse(JSON.stringify(this.nluTrainingColumns));

        setTimeout(()=>{
          if(this.$refs.conversationsTable) this.$refs.conversationsTable.refreshTable()
          if(this.$refs.containedTable) this.$refs.containedTable.refreshTable()
          if(this.$refs.handedOffTable) this.$refs.handedOffTable.refreshTable()
           //Hide by OC-556 base on OC-418
          // if(this.$refs.understandingTable) this.$refs.understandingTable.refreshTable()
          if(this.$refs.goalsTable) this.$refs.goalsTable.refreshTable()
          if(this.$refs.feedbackTable) this.$refs.feedbackTable.refreshTable()
          if(this.$refs.issuesTable) this.$refs.issuesTable.refreshTable()
          if(this.$refs.customerBehaviourTable) this.$refs.customerBehaviourTable.refreshTable()
          if(this.$refs.corpusTable) this.$refs.corpusTable.refreshTable()
          if(this.$refs.responseTable) this.$refs.responseTable.refreshTable()
          if(this.$refs.nluTrainingTable) this.$refs.nluTrainingTable.refreshTable()
          this.loading = false;
        }, 500)

      }
    },

    mergeAllSummaryResult(originList, list) {
      originList.forEach((item, index)=>{
        list.forEach(mergeItem=>{
          if(item.name == mergeItem.name) {
            this.allSummaryResults[index] = mergeItem
          }
        })
      })
    }
  },

  async created() {  
    this.initDate = new Date()  
    this.pagePanels = this.companyConfig ? (((this.companyConfig.appPages || {}).vaoOverViewPage || {}).panels || {}) : {};
    await this.handleFilterChange();

    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if(this[event.name]) this[event.name]();
    })
  },

  destroyed() {
    if(this.isLoggedIn) {
      this.$store.dispatch('amplitudeLogWithFilters', {name: 'optimization_overview_page_time_spent', data: {time_spent: WyUtils.getTimeDiff(this.initDate)}});
    }
  },
});
</script>

<style lang="scss">
  .wy-va-optimization {
    font-weight: 500;
    font-size: 14px;
    padding-top: 0px;

    .wy-genaric-table .wy-table-main thead th:nth-last-child(2) {
      width: 20%;
    }
    .wy-genaric-table .wy-table-main td:nth-last-child(2) {
      width: 20%;
    }
    .wy-genaric-table .wy-table-main thead th:nth-last-child(3) {
      width: 20%;
    }
    .wy-genaric-table .wy-table-main td:nth-last-child(3) {
      width: 20%;
    }
    .wy-genaric-table .wy-table-main thead th:nth-last-child(4) {
      width: 20%;
    }
    .wy-genaric-table .wy-table-main td:nth-last-child(4) {
      width: 20%;
    }
    .wy-genaric-table .wy-table-main thead .wy-header span {
      padding-left: 10px !important ;
    }
    .wy-genaric-table tbody tr td {
      padding: 1px 0px 1px 10px;
    }
    .wy-genaric-table tbody tr  {
      cursor: default;
    }
    .target{
      padding-left: 14px !important;
    }
    thead {
      th{
        padding-right: 5px !important;
      }
      .wy-header{
        .total{
          position: relative;
          right: 5px;
        }
      }
    }
    .wy-va-optimization-small-title {
      display: none;
      padding: 26px 23px 0px 26px;
      background-color: #F4F6F8;
      color: var(--site-font-secondary-color);
      font-size: 15px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: 400;
    }
    .annotation-text {
      font-weight: 600;
    }
    .wy-va-optimization-title {
      color: var(--site-font-secondary-color);
      font-size: 25px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: 600;
      padding-top: 0px;
      padding: 0px 23px 35px 0px;
      background-color: #FFF;
      height: 25px;
      line-height: 0.7;
    }
    .wy-va-opitization-main-content {
      // background-color: #F4F6F8;
      background-color: #FFF;
      height: unset;

      .wy-va-opitization-chart-section{
        box-sizing: border-box;
        // background-color: #F4F6F8;
        background-color: #FFF;
        border: 1px solid #CAD2DA;
        border-radius: 5px;
        padding-top: 0px;
        margin-bottom: 20px;
        height: 524px;

        .optimizationTrendChart {
          width: 100%;
          .svg-container {
            .main-svg {
              background-color: transparent !important;

              .bglayer .bg {
                fill: transparent !important;
              }
            }
          }
        }
        .js-plotly-plot .plotly, .js-plotly-plot .plotly div {
          font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
        }
        .xtick text, .ytick text, .legendtext, .annotation-text {
          font-family: "Helvetica Neue", Helvetica, Arial, sans-serif !important;
        }
      }
      .no-result-found {
        height: 327px !important;
        border: 1px solid #CAD2DA;
        .no-data {
          margin-top: 75px;
          background-size: 95px;
          height: 100px;
        }
        .el-loading-mask {
          .el-loading-spinner {
            position: initial;
            margin-top: 200px;
            // margin-top: 180px;
          }
        }
      }
    }

    .wy-optimization-table-section {
      margin-bottom: 50px;
      margin: 10px 0px 30px 0px;
      padding-bottom: 20px;

      .wy-optimization-table-section-header {
        display: flex;
        // padding-bottom: 5px;
        .wy-optimization-table-filter {
          margin-left: auto;

          .compare-by-title {
            padding-top: 6px;
            margin-left: 30px;
            margin-right: 2px;
            font-size: 10px;
          }
          .view-date-range-title {
            padding-top: 6px;
            margin-right: 2px;
            font-size: 10px;
          }
          .compare-by-dropdown .el-input__inner {
            width: 80px;
            padding-left: 10px;
            padding-right: 20px;
          }
          .date-range-dropdown .el-input__inner {
            width: 100px;
            padding-left: 10px;
            padding-right: 20px;
          }
          input.el-input__inner {
              border-width: 0px 0px 0px 0px !important;
          }
          .el-select .el-input.is-focus .el-input__inner {
            border-color: transparent;
          }
          .el-input--mini {
            font-size: 10px;
          }
          .el-input--mini .el-input__inner {
            height: 22px;
          }
          .el-select .el-input__inner:focus {
            border-color:transparent;
          }
          .el-input--mini .el-input__icon {
            line-height: 23px;
          }
          .el-select.el-select--mini {
            border: 0.5px solid #0C2837;
            box-sizing: border-box;
            border-radius: 3px;
            padding: 0px 3px;
          }
          .el-select__caret {
            background-image: url('../../assets/icons/icon_dropdown_arrow_down.svg');
            background-repeat: no-repeat;
            background-size: 8px 5px;
            background-position-x: 3px;
            background-position-y: 9px;
            width: 11px;
            height: 18px;
            vertical-align: middle;
            transform: rotate(0);
          }
          .el-icon-arrow-up:before {
            content: "";
          }
        }
      }
    }

    .optimization-details {
      padding-right: 25px;
      padding-bottom: 20px;
      min-width: 500px;

      .optimization-details-table {
        width: 100%;
        position: relative !important;
        top: 55px;
        td {
          text-align: center;
          width: 25%;
        }
      }
    }
    .wy-genaric-table tbody tr .align-right div {
      margin-left: unset !important;
      display: flex !important;
      padding-left: 5px !important;
      justify-content: center !important;
    }
    .wy-genaric-table {
      width: calc(100% - 0px);
      margin-top: 10px;
      padding: 0 0px 0 0px;
      border-radius: 6px;
      // box-shadow: 3px 3px 12px #9d9cc759;
      
      thead {
        border-bottom: 1px solid #ddd;
        .wy-header {
          font-size: 12px;
          font-weight: 600;
          max-width: 928px;
        }
        span.dynamicFields {
          width: fit-content;
          float: right;
          padding-right: 2px;
        }
      }
      .wy-table-fixed tbody tr td:first-child {
        background-color: #FFF;
      }
      tbody {
        font-size: 12px;

        tr td {
          border-bottom: 0px !important;
        }
        td.fixed {
          color: var(--site-font-tertiary-color);
          font-size: 14.22px;
          font-weight: 400 !important;
        }
        td div.dynamicFields {
          width: fit-content;
          float: right;
          padding-right: 5px;
        }
        tr td.last-child .icon-red-check {
          background-position-y: 1px;
          background-position-x: 6px;
        }
        tr td.last-child .icon-green-check {
          background-position-y: 2px;
          background-position-x: 6px;
        }
      }

      table.wy-table-main {
        border-spacing: 0px;

        .ct-series-a .ct-bar, .ct-series-a .ct-line, .ct-series-a .ct-point, .ct-series-a .ct-slice-donut {
            stroke: #4d50e8;
        }
        .ct-line {
            stroke-width: 1px;
        }
        .wy-body.no-data-body .el-loading-mask {
          margin-top: 40px;
        }
      }
      table.wy-table-main thead {
        border-bottom: 1px solid #ddd;
        width: 100%;
      }
      table.wy-table-main tbody {   
        // max-height: 300px;  
        height: 100%;
        width: 100%;
        min-width: 690px;
      }
      table.wy-table-main {
        border-radius: 6px;
      }
      
      .wy-table-wrapper {
        width: auto !important;
        height: 100%;
        padding: 0px 0px 0px 0px;
        // margin-bottom: 18px;
        background: #FFFFFF;
        border: 1px solid #CAD2DA;
        border-radius: 6px;
        overflow-y: hidden;
        
        thead tr th {
          color: #000000;
          font-size: 16px;
          font-family: 'Helvetica Neue', sans-serif;
          background-color: #FFF;
        }
      }
      .wy-table-fixed {
        top: 0px;
        padding: 0px 6px;
        border-radius: 6px;
        margin-top: 0px;
        margin-bottom: 0px;
        border-left: 1px solid #FFF;
      }
      .wy-table-fixed.show-fixed-table-shadow {
        -webkit-box-shadow: 3px 3px 12px #9d9cc759;
        box-shadow: 3px 3px 12px #9d9cc759;
        background-color: white;
        border-left: 1px solid #eeeeeead;
        padding: 0px 0px 0px 6px;
      }
    }
  }
  .table-filter-dropdown.compare-by {
    left: -70px !important;
  }
  .table-filter-dropdown.date-range {
    left: -30px !important;
  }
  
  .table-filter-dropdown {
    width: 158px;
    top:  16px !important;

    .el-select-dropdown__item {
      height: 23px;
      line-height: 23px;
      font-size: 8px;
      padding: 0 10px;
      margin: 2px 10px 0px 10px;

      span {
        line-height: 23px;
      }
    }

    .el-select-dropdown__item.selected {
      border: 0.5px solid #214586;
      box-sizing: border-box;
      border-radius: 3px;
      background-image: url('../../assets/icons/icon-blue-check.svg') !important;
      background-repeat: no-repeat;
      background-position-y: 6px;
      background-position-x: 120px;
    }
    .el-select-dropdown__item:hover {
      background-color: #E5EAF2 !important;
      border-radius: 3px;
      background-repeat: no-repeat;
      background-position-y: 6px;
      background-position-x: 120px;
    }

    .el-select-dropdown__item.hover {
      background-color: unset;
      border-radius: 3px;
      background-repeat: no-repeat;
      background-position-y: 6px;
      background-position-x: 120px;
    }
  }
  // CSS styles related to svg charts generated by Chartist library from the backend
  .ct-area {
    fill-opacity: 0.5;
  }
  .ct-line {
    stroke-width: 1px;
  }
  .ct-series-a .ct-area, .ct-series-a .ct-slice-donut-solid, .ct-series-a .ct-slice-pie {
    fill: #e6e7f8;
  }
  .ct-series-a .ct-bar, .ct-series-a .ct-line, .ct-series-a .ct-point, .ct-series-a .ct-slice-donut {
    stroke: #4D50E8;
  }
  .ct-series-b .ct-area, .ct-series-b .ct-slice-donut-solid, .ct-series-b .ct-slice-pie {
    fill: #e8f1ff;
  }
  .ct-series-b .ct-bar, .ct-series-b .ct-line, .ct-series-b .ct-point, .ct-series-b .ct-slice-donut {
    stroke: #4d50e8;
  }
  .ct-series-c .ct-area, .ct-series-c .ct-slice-donut-solid, .ct-series-c .ct-slice-pie {
    fill: none;
  }
  .ct-series-c .ct-bar, .ct-series-c .ct-line, .ct-series-c .ct-point, .ct-series-c .ct-slice-donut {
    stroke: black;
    stroke-dasharray: 3px, 3px;
    stroke-width: 0.5px;
  }

  .js-plotly-plot .plotly .main-svg {
    background-color: transparent !important;
  }

  @media screen and (max-width: 1655px) {
    .optimization-page {
      table.wy-table-main {
        tbody tr {
          width: calc(100vw - 356px);
        }
        tbody tr:hover{
          background-color: #f5f7fa !important;
        }
        thead tr {
          width: calc(100vw - 356px);
        }
      }
    }
  }
  @media screen and (min-width: 1655px) {
    .wy-va-optimization {
      width: 100%;
      height: 100%;
    }
    .optimization-page {
      // display: inline-flex;
      width: 100%;
      height: 100%;
    }

    .wy-va-optimization .wy-va-opitization-chart-section {
      // min-width: 650px !important;
      // width: 100%;
      display: flex;
      height: 324px !important
    }

    .wy-va-optimization .wy-va-opitization-main-content {
      // width: 45%;
      // min-width: 650px;
      // height: 907px;
    }

    .optimizationTrendChart {
      width: 55% !important;
    }

    .wy-va-optimization .wy-optimization-table-section {
      // display: flex;
      // flex-direction: column;
      // justify-content: flex-start;
      // flex-wrap: wrap;
      // max-height: 1380px;
      // // max-height: 1252px;
      // // max-height: 1152px;
      // padding-bottom: 15px;
      column-count: 2;
    }


    .wy-va-optimization .optimization-details {
      padding-left: 0px;
      padding-right: 0px;
      width: 45%;
      min-width: 650px;
    }

    .js-plotly-plot .plotly .main-svg {
      background-color: transparent !important;
      width: auto;

    }

    .wy-va-optimization .wy-genaric-table .wy-table-main {
      max-width: unset;
    }

    .wy-genaric-table .wy-table-main tbody {
      border-bottom: 0px solid #eee;
      tr {
        max-width: unset;
      }
    }

    .wy-va-optimization .wy-genaric-table:first-child {
      // margin-top: 0px;
    }

    .wy-va-optimization .wy-genaric-table {
      // width: 48.5%;
      display: inline-block;
      max-width: unset;
      margin: 15px 22px -4px 0px;
      // min-width: 722px;

      .wy-table-main thead tr {
        width: unset;
        max-width: unset;
      }
      .wy-table-main tbody tr {
        width: unset;
        max-width: unset;
      }
      .wy-table-fixed tbody td:nth-last-child(1) {
        padding-left: 10px;
      }
      .wy-table-fixed thead th:nth-last-child(1) {
        padding-left: 10px;
      }
    }

    .wy-va-optimization .wy-genaric-table.conversations-table table.wy-table-main tbody {
      // max-height: 230px;
    }
    .wy-va-optimization .wy-genaric-table.messages-table table.wy-table-main tbody {
      // max-height: 230px;
    }
    .wy-va-optimization .wy-genaric-table.corpus-table table.wy-table-main tbody {
      // max-height: 230px;
    }
  }

  @media screen and (min-width: 2335px) {
    .wy-table-main tbody tr {
      width: calc(100vw - 1389px);
    }
    .wy-table-main tbody tr:hover {
      background-color: #f5f7fa !important;
    }
  }
</style>